import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';

const MonitoringModal = ({ open, onClose, onSubmit }) => {
  const emptyForm = {
    parameter: '',
    frequency: '',
    thresholds: '',
    responsibleRole: '',
    duration: ''
  };

  const [formData, setFormData] = useState({ ...emptyForm });
  const [monitoringItems, setMonitoringItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const formRef = useRef(null);

  const handleAddToList = (e) => {
    e.preventDefault();
    if (!formData.parameter || !formData.frequency) return;

    if (editingId) {
      // Update existing item
      setMonitoringItems((prev) =>
        prev.map((item) =>
          item.id === editingId ? { ...item, ...formData } : item
        )
      );
      setEditingId(null);
    } else {
      // Add new item
      setMonitoringItems((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          ...formData,
          addToTaskList: false,
        },
      ]);
    }
    setFormData({ ...emptyForm });
  };

  const handleToggleItemTaskList = (id) => {
    setMonitoringItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, addToTaskList: !item.addToTaskList } : item))
    );
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleRemove = (id) => {
    setMonitoringItems((prev) => prev.filter((item) => item.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setFormData({ ...emptyForm });
    }
  };

  const handleEdit = (item) => {
    setFormData({
      parameter: item.parameter,
      frequency: item.frequency,
      thresholds: item.thresholds || '',
      responsibleRole: item.responsibleRole || '',
      duration: item.duration || ''
    });
    setEditingId(item.id);
  };

  useEffect(() => {
    if (editingId && formRef.current) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [editingId]);

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ ...emptyForm });
  };

  const handleSaveCarePlan = () => {
    if (!monitoringItems.length) return;
    const hasTaggedItems = monitoringItems.some((item) => item.addToTaskList);
    onSubmit({ monitoringItems }, { addToTaskList: hasTaggedItems });
    setMonitoringItems([]);
    setFormData({ ...emptyForm });
    onClose();
  };

  const handleClose = () => {
    setMonitoringItems([]);
    setFormData({ ...emptyForm });
    setEditingId(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Monitoring</DialogTitle>
      <form onSubmit={handleAddToList}>
        <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Box ref={formRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Parameter to Monitor *
              </Typography>
              <TextField
                fullWidth
                size="small"
                required
                placeholder="e.g., Blood Glucose, Blood Pressure"
                value={formData.parameter}
                onChange={handleChange('parameter')}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Monitoring Frequency *
              </Typography>
              <TextField
                fullWidth
                size="small"
                required
                placeholder="e.g., Every 4 hours, Twice daily"
                value={formData.frequency}
                onChange={handleChange('frequency')}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Thresholds for Alert <Typography component="span" color="text.secondary">(Optional)</Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., BP > 140/90, Glucose > 200 mg/dL"
                value={formData.thresholds}
                onChange={handleChange('thresholds')}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Responsible Role <Typography component="span" color="text.secondary">(Optional)</Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., Nurse, Resident, ICU Staff"
                value={formData.responsibleRole}
                onChange={handleChange('responsibleRole')}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Duration of Monitoring <Typography component="span" color="text.secondary">(Optional)</Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., 48 hours, Until discharge"
                value={formData.duration}
                onChange={handleChange('duration')}
              />
            </Box>

            {monitoringItems.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Monitoring items in this care plan ({monitoringItems.length}) – tag to add to task list
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {monitoringItems.map((item) => (
                    <Paper
                      key={item.id}
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: item.addToTaskList ? 'primary.main' : 'divider',
                        backgroundColor: item.addToTaskList ? 'primary.50' : 'grey.50',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: 0 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={item.addToTaskList}
                              onChange={() => handleToggleItemTaskList(item.id)}
                              sx={{ mt: -0.5 }}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                              Add to task list
                            </Typography>
                          }
                          sx={{ mt: -0.5, mr: 1 }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.parameter}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {`Frequency: ${item.frequency}${item.thresholds ? ` • Threshold: ${item.thresholds}` : ''}`}
                          </Typography>
                          {(item.responsibleRole || item.duration) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {`${item.responsibleRole ? `Role: ${item.responsibleRole}` : ''}${item.responsibleRole && item.duration ? ' • ' : ''}${item.duration ? `Duration: ${item.duration}` : ''}`}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Tooltip title="Edit monitoring item">
                          <IconButton
                            size="small"
                            color={editingId === item.id ? 'primary' : 'default'}
                            onClick={() => handleEdit(item)}
                            aria-label="Edit monitoring item"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          aria-label="Remove monitoring item"
                          onClick={() => handleRemove(item.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          {editingId && (
            <Button onClick={handleCancelEdit} variant="outlined">Cancel Edit</Button>
          )}
          <Button type="submit" variant="outlined">{editingId ? 'Update Item' : 'Add to List'}</Button>
          <Button
            onClick={handleSaveCarePlan}
            variant="contained"
            disabled={!monitoringItems.length}
          >
            Save Care Plan
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MonitoringModal;
